/**
 * Значения взяты из живых логов бота (var/live/*.jsonl) и из утверждённого
 * макета «Dashboard A Editorial». Здесь ничего не выдумано: если число не
 * подтверждено замером, оно помечено как no_data / not_measurable, а не
 * заполнено правдоподобной цифрой.
 */
import type {
  Asset,
  DepthLimiter,
  DirectionKey,
  Market,
  PnlLine,
  PoolSnapshot,
  Solver,
  TradeRow,
  WalletHolding,
  WindowKey,
} from '../../types'

export const MARKETS: Market[] = ['ETH-USDC', 'ETH-USDT', 'USDC-USDT']
export const WINDOWS: WindowKey[] = ['90s', '15m', '1h', '24h']

export interface MarketMeta {
  seed: number
  /** подпись размера, под который посчитаны обе стороны книги */
  sizeLabel: string
  /** опорная цена рынка, quote за base */
  ref: number
  /** половина спреда книги на минимальном размере, bps */
  halfSpreadBps: number
  /**
   * Насколько мид уходит за один шаг ряда, bps. Задаётся по рынку, потому что
   * отставание Uniswap стоит ровно столько, сколько цена успевает пройти за
   * это отставание: на USDC-USDT книга шириной в один тик и почти не движется,
   * на ETH-парах движение за секунду сопоставимо со спредом.
   */
  driftStepBps: number
  bookAgeLabel: string
}

export const MARKET_META: Record<Market, MarketMeta> = {
  'ETH-USDC': {
    seed: 7,
    sizeLabel: '1 ETH ≈ $1 881',
    ref: 1881.4732012,
    halfSpreadBps: 1.15,
    driftStepBps: 1.4,
    bookAgeLabel: 'book 120 ms p50 · 466 p95',
  },
  'ETH-USDT': {
    seed: 11,
    sizeLabel: '1 ETH ≈ $1 883',
    ref: 1883.2919124,
    halfSpreadBps: 1.25,
    driftStepBps: 1.5,
    bookAgeLabel: 'book 138 ms p50 · 512 p95',
  },
  'USDC-USDT': {
    seed: 13,
    sizeLabel: '$10 000',
    ref: 1.000839895,
    halfSpreadBps: 0.5,
    driftStepBps: 0.3,
    bookAgeLabel: 'book 96 ms p50 · 388 p95',
  },
}

/** Реальный набор проб глубины. ETH-размеры — в ETH, а не в долларах. */
export const PROBE_SIZES: Record<Market, number[]> = {
  'ETH-USDC': [0.01, 0.1, 0.5, 1, 2, 3, 5, 7, 10],
  'ETH-USDT': [0.01, 0.1, 0.5, 1, 2, 3, 5, 7, 10],
  'USDC-USDT': [100, 1000, 5000, 10000, 50000, 100000],
}

export interface DirSeed {
  key: DirectionKey
  sumUsd: number
  topRungUsd: number
  rungs: number
  mode: 'aggregate' | 'incremental' | 'single_rung'
  price: number
  firmPrice: number
  settleableUsd: number
  projectedLtvPct: number | null
  ageSec: number
  limiter: DepthLimiter
  note: '' | 'at cap' | 'withdrawn'
}

/**
 * Опубликованные уровни — один снапшот целиком, чтобы произведение пары
 * сходилось с ценами в той же таблице.
 */
export const DIR_SEEDS: DirSeed[] = [
  {
    key: '1:USDC->USDT',
    sumUsd: 7386.02,
    topRungUsd: 2954.41,
    rungs: 4,
    mode: 'aggregate',
    price: 1.000839895,
    firmPrice: 1.000731,
    settleableUsd: 6120,
    // Маршрут repay+withdraw: запас LTV не тратится вовсе.
    projectedLtvPct: null,
    ageSec: 2.4,
    limiter: 'pool_liquidity',
    note: '',
  },
  {
    key: '1:USDT->USDC',
    sumUsd: 424.81,
    topRungUsd: 212.4,
    rungs: 2,
    mode: 'incremental',
    price: 0.998931024,
    firmPrice: 0.998864,
    settleableUsd: 424.81,
    projectedLtvPct: 79.4994,
    ageSec: 4.1,
    limiter: 'ltv_cap_own',
    note: 'at cap',
  },
  {
    key: '1:WETH->USDC',
    sumUsd: 425.83,
    topRungUsd: 425.83,
    rungs: 1,
    mode: 'single_rung',
    price: 1881.4732012,
    firmPrice: 1881.6104,
    settleableUsd: 425.83,
    projectedLtvPct: 79.4997,
    ageSec: 1.8,
    limiter: 'ltv_cap_own',
    note: 'at cap',
  },
  {
    key: '1:USDC->WETH',
    sumUsd: 3001.95,
    topRungUsd: 1200.78,
    rungs: 3,
    mode: 'aggregate',
    price: 0.000530741,
    firmPrice: 0.000530612,
    settleableUsd: 2480,
    projectedLtvPct: 78.2931,
    ageSec: 3.3,
    limiter: 'unknown',
    note: '',
  },
  {
    key: '1:WETH->USDT',
    sumUsd: 2999.91,
    topRungUsd: 1499.96,
    rungs: 2,
    mode: 'incremental',
    price: 1883.2919124,
    firmPrice: 1883.0021,
    settleableUsd: 2999.91,
    projectedLtvPct: 78.9012,
    ageSec: 6.2,
    limiter: 'unknown',
    note: '',
  },
  {
    key: '1:USDT->WETH',
    sumUsd: 426.17,
    topRungUsd: 426.17,
    rungs: 1,
    mode: 'single_rung',
    price: 0.000530229,
    firmPrice: 0.000530118,
    settleableUsd: 426.17,
    projectedLtvPct: 79.4991,
    ageSec: 31.4,
    limiter: 'unknown',
    note: 'withdrawn',
  },
]

/**
 * Снапшот пула. Один запиненный блок — все три числа согласованы только внутри
 * него. Единицы: USDC/USDT 6 знаков, WETH 18.
 *
 * Цены оракула — настоящие: USDC и USDT расходятся на 0.0998%, WETH = 1e18 по
 * построению (оракул деноминирован в WETH). USDC принят за $1.
 */
/**
 * Снапшот пула. Позиция взята с боевой витрины венью, чтобы числа на экране
 * можно было сверить с их интерфейсом строка в строку.
 *
 * Одно расхождение оставлено намеренно и подписано на экране: мы принимаем
 * USDC за $1 (оракул пула деноминирован в WETH, доллара в нём нет вообще), а
 * витрина берёт внешнюю цену, где USDC = 0.99984. Отсюда разница в залоге
 * примерно на $1.8 при полном совпадении долга.
 */
export const POOL: PoolSnapshot = {
  blockNumber: 25_763_334,
  observedAtMs: 0,
  liquidationThresholdUnits: 900_000_000_000_000_000n,
  userLtvUnits: 721_800_000_000_000_000n,
  entryFee: 1,
  makerAllowlisted: true,
  usdReferenceAsset: 'USDC',
  assets: {
    USDC: {
      symbol: 'USDC',
      decimals: 6,
      lockedCollateralUnits: 11_012_387_000n,
      debtUnits: 0n,
      walletBalanceUnits: 0n,
      poolLiquidityUnits: 48_210_000_000n,
      totalLockedDepositsUnits: null,
      availableBorrowUnits: 893_550_000n,
      // 1e18 / 525370000000000 = $1903.42 за WETH
      oraclePriceUnits: 525_370_000_000_000n,
      maxLtvUnits: 800_000_000_000_000_000n,
      pauseStatus: 0,
      borrowRatePct: 12,
    },
    USDT: {
      symbol: 'USDT',
      decimals: 6,
      lockedCollateralUnits: 419_308_400n,
      debtUnits: 7_698_743_000n,
      walletBalanceUnits: 0n,
      poolLiquidityUnits: 51_832_300_000n,
      totalLockedDepositsUnits: null,
      availableBorrowUnits: 894_600_000n,
      oraclePriceUnits: 524_754_000_000_000n,
      maxLtvUnits: 800_000_000_000_000_000n,
      pauseStatus: 0,
      borrowRatePct: 15,
    },
    WETH: {
      symbol: 'WETH',
      decimals: 18,
      lockedCollateralUnits: 0n,
      debtUnits: 295_100_000_000_000_000n,
      walletBalanceUnits: 0n,
      poolLiquidityUnits: 51_038_000_000_000_000_000n,
      totalLockedDepositsUnits: null,
      availableBorrowUnits: 469_400_000_000_000_000n,
      oraclePriceUnits: 1_000_000_000_000_000_000n,
      maxLtvUnits: 900_000_000_000_000_000n,
      pauseStatus: 2,
      borrowRatePct: 6,
    },
  },
}

/**
 * Кошелёк вне пула. ETH нельзя положить в залог напрямую — сначала обёртка,
 * и это отдельная транзакция, поэтому кнопка supply у него неактивна.
 */
export const WALLET: WalletHolding[] = [
  { symbol: 'ETH', amount: 0.01596, usd: 30.39, apyPct: 0, needsWrap: true },
]

export const SOLVERS: Solver[] = [
  'kyber_integration',
  'quasi',
  'horadrim',
  'portus',
  'ventolabs_test',
]

interface WinSeed {
  budgetSkips: number
  dataSkips: number
  capacityRefusals: number
  p50: number
  p90: number
  p99: number
  /** [солвер, отвечено (null = нет данных), оценено венью, окон ниже 0.90] */
  solvers: [Solver, number | null, number, string][]
}

/**
 * Приёмка. Знаменатель у каждого окна свой: 179/186 замерено за 31 минуту, и
 * переносить его в 90-секундное окно нельзя — при 8 RFQ/мин туда попадает
 * около дюжины запросов, и доля гуляет несопоставимо резче.
 */
export const WIN_SEEDS: Record<WindowKey, WinSeed> = {
  '90s': {
    budgetSkips: 0,
    dataSkips: 1,
    capacityRefusals: 1,
    p50: 36,
    p90: 250,
    p99: 2613,
    solvers: [
      ['kyber_integration', 11, 11, '2'],
      ['quasi', 2, 2, '—'],
      ['horadrim', 1, 1, '—'],
      ['portus', 1, 2, '9'],
      ['ventolabs_test', null, 0, '—'],
    ],
  },
  '15m': {
    budgetSkips: 0,
    dataSkips: 3,
    capacityRefusals: 4,
    p50: 31,
    p90: 243,
    p99: 1204,
    solvers: [
      ['kyber_integration', 148, 152, '3'],
      ['quasi', 22, 23, '1'],
      ['horadrim', 7, 7, '—'],
      ['portus', 2, 3, '9'],
      ['ventolabs_test', 0, 1, '1'],
    ],
  },
  '1h': {
    budgetSkips: 1,
    dataSkips: 7,
    capacityRefusals: 0,
    p50: 28,
    p90: 198,
    p99: 902,
    solvers: [
      ['kyber_integration', 492, 497, '4'],
      ['quasi', 73, 75, '2'],
      ['horadrim', 26, 26, '—'],
      ['portus', 6, 8, '11'],
      ['ventolabs_test', 1, 2, '1'],
    ],
  },
  '24h': {
    budgetSkips: 4,
    dataSkips: 62,
    capacityRefusals: 115,
    p50: 36,
    p90: 250,
    p99: 2613,
    solvers: [
      ['kyber_integration', 4187, 4302, '18'],
      ['quasi', 598, 612, '7'],
      ['horadrim', 241, 244, '2'],
      ['portus', 43, 58, '26'],
      ['ventolabs_test', 11, 17, '6'],
    ],
  },
}

/**
 * Гистограмма латентности: хвост обязателен, один выброс 2613 мс однажды убил
 * прогон по дедлайну венью 750 мс.
 */
export const LATENCY_HISTOGRAM = [3, 9, 14, 21, 17, 11, 7, 5, 4, 3, 2, 1, 1, 2]

export const LATENCY_DEADLINE_MS = 750

/**
 * Сделки. Четыре верхние — этот прогон, ниже — round-trip арбитражника
 * 15.08 22:02–22:10: девять филлов на $124 117 за семь минут парами BUY/SELL
 * почти равного размера. Это не спрос, и помечать это обязательно.
 */
// Смещения от «сейчас» в минутах — относительный порядок и разнос между
// строками сохраняют оригинальную (ранее захардкоженную часами) историю,
// сами часы больше ничего не значат.
const MIN = 60_000
export const TRADES: TradeRow[] = [
  { tsMs: Date.now() - 5 * MIN, direction: '1:USDC->USDT', notionalUsd: 198.34, grossUsd: 0.09, netUsd: -0.42, blockLabel: '25 763 301', attribution: 'rfq id', roundTrip: false, run: 'this' },
  { tsMs: Date.now() - 90 * MIN, direction: '1:WETH->USDC', notionalUsd: 95.44, grossUsd: 0.11, netUsd: -0.31, blockLabel: '25 762 984', attribution: 'rfq id', roundTrip: false, run: 'this' },
  { tsMs: Date.now() - 290 * MIN, direction: '1:USDT->USDC', notionalUsd: 28.19, grossUsd: 0.02, netUsd: -0.38, blockLabel: '25 762 108', attribution: 'price', roundTrip: false, run: 'this' },
  { tsMs: Date.now() - 488 * MIN, direction: '1:USDC->WETH', notionalUsd: 10.42, grossUsd: 0.01, netUsd: -0.36, blockLabel: '25 761 320', attribution: 'ambiguous', roundTrip: false, run: 'this' },
  { tsMs: Date.now() - 1500 * MIN, direction: '1:USDC->USDT', notionalUsd: 15091, grossUsd: 1.42, netUsd: -1.27, blockLabel: '25 690 442', attribution: 'rfq id', roundTrip: true, run: 'prev' },
  { tsMs: Date.now() - 1501 * MIN, direction: '1:USDT->USDC', notionalUsd: 15077, grossUsd: 1.38, netUsd: -1.19, blockLabel: '25 690 440', attribution: 'rfq id', roundTrip: true, run: 'prev' },
  { tsMs: Date.now() - 1503 * MIN, direction: '1:USDC->USDT', notionalUsd: 12176, grossUsd: 1.11, netUsd: -1.02, blockLabel: '25 690 331', attribution: 'rfq id', roundTrip: true, run: 'prev' },
  { tsMs: Date.now() - 1508 * MIN, direction: '1:USDC->USDT', notionalUsd: 27523, grossUsd: 2.31, netUsd: -2.09, blockLabel: '25 690 061', attribution: 'ambiguous', roundTrip: false, run: 'prev' },
]

export const TODAY = { fills: 4, turnoverUsd: 712, grossUsd: 1.63, netUsd: -1.47 }

/**
 * P&L. Маршрут (route drag) сюда НЕ входит: это другая модель — она описывает,
 * как валовая цена мейкера превращается в net пользователя, и из нашего
 * кэшфлоу не вычитается. Газ разделён: наш измерим по цепи, солверский — нет.
 */
export const PNL: PnlLine[] = [
  { label: 'gross edge', note: 'measured', valueUsd: 1.63, kind: 'item' },
  { label: 'borrow entry fee', note: 'measured', valueUsd: -0.03, kind: 'item' },
  { label: 'borrow interest', note: 'estimated', valueUsd: -0.68, kind: 'item' },
  { label: 'trading result', note: 'excl. gas', valueUsd: 0.92, kind: 'subtotal' },
  { label: 'our operations gas', note: 'chain', valueUsd: -2.39, kind: 'item' },
  { label: 'solver gas', note: 'solver pays', valueUsd: null, kind: 'na' },
  { label: 'net', note: '−20.6 bps', valueUsd: -1.47, kind: 'total' },
]

export const ASSET_ORDER: Asset[] = ['USDC', 'USDT', 'WETH']

export const PAUSE_LABEL: Record<0 | 1 | 2 | 3, string> = {
  0: 'none',
  1: 'type 1 — repay and supply off',
  2: 'type 2 — supply off',
  3: 'type 3 — outgoing leg off',
}

export const LIMITER_LABEL: Record<DepthLimiter, string> = {
  ltv_headroom: 'LTV headroom',
  ltv_cap_own: 'our own 79.5% cap',
  ltv_cap_contract: 'contract wall',
  pool_liquidity: 'pool liquidity',
  locked_collateral: 'locked collateral',
  direction_max_notional: 'direction cap',
  publish_floor: 'publish floor',
  min_edge: 'min edge',
  book_stale: 'book stale',
  pool_stale: 'pool stale',
  unhedged_weth: 'unhedged WETH limit',
  quote_slots: 'quote slots',
  pair_crossed: 'pair crossed',
  supply_cap: 'supply cap',
  paused: 'paused',
  unknown: 'unknown',
}
